import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Typography,
} from '@mui/material';
import { useShiftLookup } from '../../hooks/useShiftLookup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { format } from 'date-fns';
import { AuthService } from '../../services/auth.service';
import { DashboardService } from '../../services/dashboard.service';
import { AttendanceStatus, AttendanceStatusUser } from '../../interfaces/dashboard.interface';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';
import { Column } from '../common/responsiveTable.types';
import ResponsiveTable from '../common/ResponsiveTable';

import SearchableSelect from '../common/SearchableSelect';
/** One rendered line: a user shown on this status page. */
interface AttendanceStatusRow {
    userId: string;
    name: string;
    email: string;
    department: string;
}

// Declared in the OLD table's source order (Name, Email, Department) — `priority` only picks the
// card/compact LEAD, it never reorders table mode.
export const attendanceStatusUserColumns: Column<AttendanceStatusRow>[] = [
    { key: 'name', header: 'Name', priority: 1, helpKey: 'attendance.status.col.name', render: r => r.name },
    { key: 'email', header: 'Email', helpKey: 'attendance.status.col.email', render: r => r.email },
    { key: 'department', header: 'Department', helpKey: 'attendance.status.col.department', render: r => r.department },
];

const VALID_STATUSES: AttendanceStatus[] = ['present', 'on-leave', 'absent'];

const STATUS_TITLES: Record<AttendanceStatus, string> = {
    'present': 'Present Today',
    'on-leave': 'On Leave Today',
    'absent': 'Absent Today',
};

const isValidStatus = (value: string | undefined): value is AttendanceStatus =>
    !!value && (VALID_STATUSES as string[]).includes(value);

const AttendanceStatusUserList: React.FC = () => {
    const { status } = useParams<{ status: string }>();
    const navigate = useNavigate();
    const toast = useToast();

    const [users, setUsers] = useState<AttendanceStatusUser[]>([]);
    const [loading, setLoading] = useState(true);
    // NOTE: AttendanceStatusUser (from DashboardService) is a user-level summary and does not carry
    // per-attendance shift fields. The Shift filter dropdown is available for UI consistency but
    // cannot filter rows until the backend enriches this endpoint with shiftId per user.
    const { shifts } = useShiftLookup();

    const validStatus: AttendanceStatus | null = useMemo(
        () => (isValidStatus(status) ? status : null),
        [status]
    );

    useEffect(() => {
        if (!validStatus) {
            setLoading(false);
            return;
        }
        const fetchUsers = async () => {
            try {
                setLoading(true);
                const institutionId = AuthService.getSelectedInstitution();
                const result = await DashboardService.getAttendanceStatusUsers(institutionId, validStatus);
                setUsers(result);
            } catch (err) {
                toast.showApiError(err, 'Failed to load user list');
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [validStatus, toast]);

    if (!validStatus) {
        return (
            <Box sx={{ padding: 3 }}>
                <Alert severity="error">
                    Invalid status "{status}". Expected one of: present, on-leave, absent.
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const rows: AttendanceStatusRow[] = users.map(user => ({
        userId: user.userId,
        name: displayUsername(user.name) || '-',
        email: user.email || '-',
        department: user.department || '-',
    }));

    return (
        <Box data-help-page="attendance.status.page" sx={{ padding: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="h5" fontWeight="bold">
                        {STATUS_TITLES[validStatus]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {format(new Date(), 'dd-MM-yyyy')} · {users.length} user{users.length === 1 ? '' : 's'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Shift filter shown for UI consistency. Rows cannot be filtered by shift here
                        because AttendanceStatusUser does not carry shiftId; awaiting backend enrichment. */}
                    <SearchableSelect
                        options={shifts.filter(s => s.isActive)}
                        value=""
                        onChange={() => {}}
                        getOptionLabel={s => `${s.code} – ${s.name}`}
                        label="Shift"
                        placeholder="All shifts"
                        disabled
                        data-help="attendance.status.shiftFilter"
                        sx={{ flex: '1 1 160px', maxWidth: 260 }}
                    />
                    <Button
                        data-help="attendance.status.back"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/')}
                    >
                        Back to Dashboard
                    </Button>
                </Box>
            </Box>

            {users.length === 0 ? (
                <Alert severity="info">No users match this status today.</Alert>
            ) : (
                // Shift, Instance Status, Late, OT columns are not available on this page:
                // AttendanceStatusUser is a user-level daily summary and does not carry
                // per-attendance shift enrichment fields. They will be added once the
                // DashboardService endpoint is enriched (future task).
                <ResponsiveTable<AttendanceStatusRow> data-help-section="attendance.status.table"
                    pageKey="attendance.status"
                    columns={attendanceStatusUserColumns}
                    rows={rows}
                    getRowKey={r => r.userId}
                    emptyMessage="No users match this status today."
                />
            )}
        </Box>
    );
};

export default AttendanceStatusUserList;
